import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportError } from 'helpers/error-logging';

import { PARTNER_STATE } from 'features/Sku/Items/SkuItems.constants';

import modifierGateway from './Modifiers.gateway';
import { modifierKeys } from './Modifiers.queryKeys';
import useModifiersReadOnly from './useModifiersReadOnly';

const applySoldOut = (groups, { modifierId, soldOut }) =>
  (groups || []).map(group => ({
    ...group,
    modifiers: group.modifiers.map(modifier => (modifier.core.id === modifierId ? { ...modifier, soldOut } : modifier)),
  }));

const applyGroupEnabled = (groups, { groupId, enabled }) =>
  (groups || []).map(group => (group.core.id === groupId ? { ...group, enabled } : group));

const READ_ONLY_REFUSAL_NAME = 'ModifiersReadOnlyRefusal';

const isReadOnlyRefusal = error => error?.name === READ_ONLY_REFUSAL_NAME;

export default function useModifierMutations({ venueId }) {
  const queryClient = useQueryClient();
  const { isReadOnly } = useModifiersReadOnly();
  const groupsKey = modifierKeys.groups(venueId);
  // UI gating alone is not enough: a control that misses its gate would still hit the network.
  // Refusing here keeps integrated venues read-only for every mutation, present and future. The
  // named error keeps the refusal out of error reporting and cache invalidation below.
  const writable =
    mutationFn =>
    (...args) => {
      if (!isReadOnly) return mutationFn(...args);
      const refusal = new Error('Modifier writes are disabled for integrated venues');
      refusal.name = READ_ONLY_REFUSAL_NAME;
      return Promise.reject(refusal);
    };

  const reportUnlessRefused = message => error => {
    if (!isReadOnlyRefusal(error)) reportError(message, error);
  };

  // Deliberately not returning the promise: React Query awaits whatever onSettled returns before
  // it settles mutateAsync, so a failed save would spin until the refetch and its retries finished.
  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: groupsKey });
  };

  const invalidateGroupsUnlessRefused = (data, error) => {
    if (!isReadOnlyRefusal(error)) invalidateGroups();
  };

  const createGroup = useMutation({
    mutationFn: writable(group => modifierGateway.createModifierGroup(venueId, group)),
    onError: reportUnlessRefused('Error creating SKU modifier group'),
    onSettled: invalidateGroupsUnlessRefused,
  });

  const setModifierSoldOut = useMutation({
    mutationFn: writable(({ modifierId, soldOut }) => modifierGateway.updateModifier(venueId, modifierId, { soldOut })),
    // onMutate runs before the mutationFn refuses, so the optimistic flip must skip too or the
    // cache would flicker before rolling back.
    onMutate: async variables => {
      if (isReadOnly) return undefined;
      await queryClient.cancelQueries({ queryKey: groupsKey });
      const snapshot = queryClient.getQueryData(groupsKey);
      queryClient.setQueryData(groupsKey, current => applySoldOut(current, variables));
      return { snapshot };
    },
    onError: (error, variables, context) => {
      if (context) queryClient.setQueryData(groupsKey, context.snapshot);
      reportUnlessRefused('Error updating SKU modifier availability')(error);
    },
    onSettled: invalidateGroupsUnlessRefused,
  });

  const updateGroup = useMutation({
    mutationFn: writable(({ groupId, group }) => modifierGateway.updateModifierGroup(venueId, groupId, group)),
    onError: reportUnlessRefused('Error updating SKU modifier group'),
    onSettled: invalidateGroupsUnlessRefused,
  });

  // Optimistic like setModifierSoldOut: the library row is a controlled dropdown, so the cache
  // must flip immediately or React snaps it back before the refetch lands.
  const setGroupEnabled = useMutation({
    mutationFn: writable(({ groupId, enabled }) => modifierGateway.updateModifierGroup(venueId, groupId, { enabled })),
    onMutate: async variables => {
      if (isReadOnly) return undefined;
      await queryClient.cancelQueries({ queryKey: groupsKey });
      const snapshot = queryClient.getQueryData(groupsKey);
      queryClient.setQueryData(groupsKey, current => applyGroupEnabled(current, variables));
      return { snapshot };
    },
    onError: (error, variables, context) => {
      if (context) queryClient.setQueryData(groupsKey, context.snapshot);
      reportUnlessRefused('Error updating SKU modifier group availability')(error);
    },
    onSettled: invalidateGroupsUnlessRefused,
  });

  const linkGroupToItems = useMutation({
    mutationFn: writable(({ groupId, itemIds }) => modifierGateway.linkModifierGroupToItems(venueId, groupId, itemIds)),
    onError: reportUnlessRefused('Error linking SKU modifier group to items'),
    onSettled: invalidateGroupsUnlessRefused,
  });

  // No invalidate-on-settled, unlike the mutations above: the editor refreshes once at the end
  // rather than refetching the library between every option.
  const createModifier = useMutation({
    mutationFn: writable(({ groupId, modifier }) => modifierGateway.createModifier(venueId, groupId, modifier)),
    onError: reportUnlessRefused('Error creating SKU modifier'),
  });

  const updateModifier = useMutation({
    mutationFn: writable(({ modifierId, updates }) => modifierGateway.updateModifier(venueId, modifierId, updates)),
    onError: reportUnlessRefused('Error updating SKU modifier'),
  });

  const removeModifier = useMutation({
    mutationFn: writable(({ modifierId }) =>
      modifierGateway.updateModifier(venueId, modifierId, { partnerState: PARTNER_STATE.DELETED })
    ),
    onError: reportUnlessRefused('Error removing SKU modifier'),
  });

  const unlinkGroupFromItem = useMutation({
    mutationFn: writable(({ groupId, itemId }) =>
      modifierGateway.unlinkModifierGroupFromItem(venueId, groupId, itemId)
    ),
    onError: reportUnlessRefused('Error unlinking SKU modifier group from item'),
    onSettled: invalidateGroupsUnlessRefused,
  });

  return {
    createGroup,
    createModifier,
    linkGroupToItems,
    refreshGroups: invalidateGroups,
    removeModifier,
    setGroupEnabled,
    setModifierSoldOut,
    unlinkGroupFromItem,
    updateGroup,
    updateModifier,
  };
}
